import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { Board, Move, IPiece, Position, PieceType } from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 5: King Safety Move
 *
 * CRITICAL: If the AI is choosing to move its king into direct danger,
 * it must filter out these dangerous moves! This prevents the king from
 * being captured unnecessarily.
 */
export class KingSafetyMovePriority extends BaseDecisionPriority {
  readonly priority = 6;
  readonly name = "King Safety Move";
  readonly description =
    "CRITICAL: Prevent king from moving into immediate danger";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we must always check for king safety
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const playerColor = isPlayerTurn ? "Light" : "Dark";

    // Find our king first
    const ourKing = this.findOurKing(gameState, isPlayerTurn);
    if (!ourKing) {
      this.addLog(`❌ No king found for ${playerColor}`);
      return {
        shouldEarlyExit: false,
        reasoning: "No king found",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(`👑 ${playerColor} King: ${ourKing.piece.type} at [${ourKing.position.row},${ourKing.position.col}]`);

    // PRE-ANALYSIS: Scan all current enemy attack vectors on our king
    this.logEnemyAttackVectors(gameState, ourKing);

    // Find all king moves that would put the king in danger
    const dangerousKingMoves: MoveInfo[] = [];
    const safeKingMoves: MoveInfo[] = [];
    let totalKingMoves = 0;

    for (const moveInfo of context.allPossibleMoves) {
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );

      // Only check king moves
      if (!piece || !piece.isKing) continue;

      totalKingMoves++;

      if (this.isKingMoveInDanger(moveInfo, gameState)) {
        dangerousKingMoves.push(moveInfo);
      } else {
        safeKingMoves.push(moveInfo);
      }
    }

    this.addLog(`📊 ${totalKingMoves} king moves: ${safeKingMoves.length} safe, ${dangerousKingMoves.length} dangerous`);

    if (totalKingMoves === 0) {
      return {
        shouldEarlyExit: false,
        reasoning: "No king moves to check",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    if (dangerousKingMoves.length === 0) {
      return {
        shouldEarlyExit: false,
        reasoning: "All king moves are safe",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Log concise filtered-moves summary
    const filteredDescs = dangerousKingMoves.map(m =>
      `[${m.fromPosition.row},${m.fromPosition.col}]->[${m.move.position.row},${m.move.position.col}]`
    );
    this.addLog(`🛡️ Filtered: ${filteredDescs.join(', ')}`);

    if (safeKingMoves.length === 0) {
      // All king moves dangerous — use non-king moves
      const nonKingMoves = context.allPossibleMoves.filter((moveInfo) => {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, moveInfo.fromPosition);
        return !piece || !piece.isKing;
      });

      // Fallback: if no non-king moves, allow one king move to prevent crash
      if (nonKingMoves.length === 0) {
        this.addLog(`🚨 No safe moves — fallback to least dangerous king move`);
        return this.createFilterResult(
          [dangerousKingMoves[0]],
          `KING SAFETY FALLBACK: No safe alternatives`,
          { totalKingMoves, safeKingMoves: 0, movesFiltered: totalKingMoves - 1, logs: this.analysisLogs }
        );
      }

      return this.createFilterResult(
        nonKingMoves,
        `KING SAFETY: All ${totalKingMoves} king moves dangerous — using ${nonKingMoves.length} non-king moves`,
        { totalKingMoves, safeKingMoves: 0, movesFiltered: totalKingMoves, logs: this.analysisLogs }
      );
    }

    // Filter out dangerous king moves, keeping all non-king moves + safe king moves
    const safeMoves = context.allPossibleMoves.filter((moveInfo) => {
      const piece = MCTSUtils_v2.getPieceAt(gameState.board, moveInfo.fromPosition);
      if (!piece || !piece.isKing) return true;
      return !dangerousKingMoves.some(
        (d) => d.fromPosition.row === moveInfo.fromPosition.row &&
               d.fromPosition.col === moveInfo.fromPosition.col &&
               d.move.position.row === moveInfo.move.position.row &&
               d.move.position.col === moveInfo.move.position.col
      );
    });

    return this.createFilterResult(
      safeMoves,
      `KING SAFETY MOVE: Filtered ${dangerousKingMoves.length} dangerous king moves`,
      { totalKingMoves, safeKingMoves: safeKingMoves.length, movesFiltered: dangerousKingMoves.length, logs: this.analysisLogs }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
    // Console logging removed for clarity - check diagnostics.logs for analysis details
  }

  private logEnemyAttackVectors(gameState: any, ourKing: { piece: IPiece; position: Position }): void {
    this.addLog(`\n🔍 ENEMY ATTACK VECTOR SCAN on king at [${ourKing.position.row},${ourKing.position.col}]:`);

    // Flip turn to generate enemy moves
    const testState = cloneFullGameState(gameState);
    testState.isLightTurn = !ourKing.piece.isLight;
    const enemyMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState);

    // Find all enemy pieces that can reach our king
    const attackers: string[] = [];
    const nearbyPieces: string[] = [];

    for (const enemyMove of enemyMoves) {
      const attackerPiece = MCTSUtils_v2.getPieceAt(testState.board, enemyMove.fromPosition);
      if (!attackerPiece || attackerPiece.isLight === ourKing.piece.isLight) continue;

      if (enemyMove.move.position.row === ourKing.position.row &&
          enemyMove.move.position.col === ourKing.position.col) {
        const desc = `${attackerPiece.type} at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`;
        if (!attackers.includes(desc)) attackers.push(desc);
      }
    }

    // List all enemy pieces with their distances
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isLight !== ourKing.piece.isLight) {
          const dist = Math.abs(row - ourKing.position.row) + Math.abs(col - ourKing.position.col);
          nearbyPieces.push(`${piece.type}${piece.isKing ? ' (KING)' : ''} at [${row},${col}] (dist: ${dist})`);
        }
      }
    }

    this.addLog(`   Enemy pieces on board: ${nearbyPieces.length}`);
    for (const p of nearbyPieces) this.addLog(`   • ${p}`);

    if (attackers.length > 0) {
      this.addLog(`   ⚠️ DIRECT THREATS to king: ${attackers.length}`);
      for (const a of attackers) this.addLog(`   ⚔️ ${a} CAN ATTACK KING`);
    } else {
      this.addLog(`   ✅ No enemy pieces can directly attack king right now`);
    }
  }

  private findOurKing(
    gameState: any,
    isLightTurn: boolean
  ): {
    piece: IPiece;
    position: Position;
  } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isKing && piece.isLight === isLightTurn) {
          return {
            piece,
            position: { row, col },
          };
        }
      }
    }
    return null;
  }

  private isKingMoveInDanger(kingMove: MoveInfo, gameState: any): boolean {
    try {
      const from = kingMove.fromPosition;
      const to = kingMove.move.position;
      const moveDesc = `[${from.row},${from.col}]->[${to.row},${to.col}]`;

      const movingKingPiece = MCTSUtils_v2.getPieceAt(gameState.board, from);
      if (!movingKingPiece) return true;

      // Check Monkey king attacking Bear king (66% failure)
      const targetSquare = gameState.board[to.row][to.col];
      const targetPiece = targetSquare?.Pieces?.[0];

      if (targetPiece &&
          movingKingPiece.type === PieceType.Monkey &&
          targetPiece.type === PieceType.Bear &&
          targetPiece.isKing) {
        const victoryRow = movingKingPiece.isLight ? 7 : 0;
        const distToVictory = Math.abs(to.row - victoryRow);
        if (distToVictory <= 1) return false;
        this.addLog(`   ❌ ${moveDesc}: Monkey vs Bear king (66% fail, ${distToVictory} from victory)`);
        return true;
      }

      // Simulate the king move
      const testState = this.simulateKingMove(gameState, kingMove);
      if (!testState) return true;

      // Find threats at new position
      const enemyMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState);
      const guaranteedThreats: string[] = [];
      const probThreats: string[] = [];

      for (const enemyMove of enemyMoves) {
        if (enemyMove.move.position.row === to.row && enemyMove.move.position.col === to.col) {
          const attacker = MCTSUtils_v2.getPieceAt(testState.board, enemyMove.fromPosition);
          const desc = attacker
            ? `${attacker.type} at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`
            : `? at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`;

          if (attacker?.type === PieceType.Monkey && movingKingPiece.type === PieceType.Bear) {
            probThreats.push(desc);
          } else {
            guaranteedThreats.push(desc);
          }
        }
      }

      if (guaranteedThreats.length > 0) {
        this.addLog(`   ❌ ${moveDesc}: ${guaranteedThreats[0]} can capture king`);
        return true;
      }

      if (probThreats.length > 0) {
        const victoryRow = movingKingPiece.isLight ? 7 : 0;
        const distToVictory = Math.abs(to.row - victoryRow);
        if (distToVictory <= 1) return false;
        this.addLog(`   ❌ ${moveDesc}: ${probThreats[0]} (33% risk, ${distToVictory} from victory)`);
        return true;
      }

      // 2-PLY CHECK: Even if no immediate threat, does any enemy response
      // create a forced king capture with no escape?
      if (this.isKingTrappedIn2Ply(testState, to, movingKingPiece, enemyMoves)) {
        this.addLog(`   ❌ ${moveDesc}: 2-ply forced king capture detected — enemy can trap king`);
        return true;
      }

      return false; // Safe
    } catch (error) {
      return true;
    }
  }

  /**
   * 2-PLY KING SAFETY: Check if ANY enemy response creates a forced capture.
   * After our king moves to kingPosition, we check each nearby enemy move:
   *   1. Simulate enemy response
   *   2. Is our king now threatened?
   *   3. If yes, can our king escape OR can a friendly piece neutralize?
   *   4. If no escape and no neutralization → forced 2-move capture
   */
  private isKingTrappedIn2Ply(
    stateAfterKingMove: any,
    kingPosition: Position,
    kingPiece: IPiece,
    enemyMoves: { fromPosition: Position; move: { position: Position; moveType?: string } }[]
  ): boolean {
    const moveService = MCTSMoveGenerationService.getInstance();

    for (const enemyMove of enemyMoves) {
      // Optimization: skip enemy moves far from king — they can't create immediate threats
      const moveDist =
        Math.abs(enemyMove.move.position.row - kingPosition.row) +
        Math.abs(enemyMove.move.position.col - kingPosition.col);
      if (moveDist > 3) continue;

      // Simulate enemy response
      const stateAfterEnemy = cloneFullGameState(stateAfterKingMove);
      const enemyPiece = getPieceFromPosition(stateAfterEnemy, enemyMove.fromPosition);
      if (!enemyPiece) continue;

      try {
        applyMoveWithSnakeRespawn(
          stateAfterEnemy,
          enemyMove.fromPosition,
          enemyMove.move as any,
          enemyPiece
        );
      } catch {
        continue;
      }

      // Check if king at kingPosition is now under threat
      const threatCheckState = cloneFullGameState(stateAfterEnemy);
      threatCheckState.isLightTurn = !kingPiece.isLight; // enemy's perspective
      const enemyAttacks = moveService.getAllMoves(threatCheckState);

      let kingThreatened = false;
      const attackerPositions: Position[] = [];
      for (const attack of enemyAttacks) {
        if (
          attack.move.position.row === kingPosition.row &&
          attack.move.position.col === kingPosition.col
        ) {
          // Monkey vs Bear has 66% failure — don't count as guaranteed threat
          const attackerPc = MCTSUtils_v2.getPieceAt(
            threatCheckState.board,
            attack.fromPosition
          );
          if (attackerPc?.type === PieceType.Monkey && kingPiece.type === PieceType.Bear) {
            continue;
          }
          kingThreatened = true;
          if (!attackerPositions.some(p => p.row === attack.fromPosition.row && p.col === attack.fromPosition.col)) {
            attackerPositions.push(attack.fromPosition);
          }
        }
      }

      if (!kingThreatened) continue;

      // King IS threatened after this enemy response — check for escapes
      stateAfterEnemy.isLightTurn = kingPiece.isLight; // our turn
      const ourMoves = moveService.getAllMoves(stateAfterEnemy);

      // Check 1: Can our king escape to a safe square?
      const kingEscapes = ourMoves.filter(
        (m) =>
          m.fromPosition.row === kingPosition.row &&
          m.fromPosition.col === kingPosition.col
      );

      let hasSafeEscape = false;
      for (const escape of kingEscapes) {
        const escapeState = cloneFullGameState(stateAfterEnemy);
        escapeState.isLightTurn = kingPiece.isLight;
        const escapingKing = getPieceFromPosition(escapeState, escape.fromPosition);
        if (!escapingKing) continue;

        try {
          applyMoveWithSnakeRespawn(
            escapeState,
            escape.fromPosition,
            escape.move as any,
            escapingKing
          );
        } catch {
          continue;
        }

        // Check if escape square is safe from enemy capture
        escapeState.isLightTurn = !kingPiece.isLight;
        const enemyAfterEscape = moveService.getAllMoves(escapeState);

        let escapeSafe = true;
        for (const counterAttack of enemyAfterEscape) {
          if (
            counterAttack.move.position.row === escape.move.position.row &&
            counterAttack.move.position.col === escape.move.position.col
          ) {
            const caPiece = MCTSUtils_v2.getPieceAt(
              escapeState.board,
              counterAttack.fromPosition
            );
            // Monkey vs Bear 66% fail — don't count as guaranteed
            if (caPiece?.type === PieceType.Monkey && kingPiece.type === PieceType.Bear) {
              continue;
            }
            escapeSafe = false;
            break;
          }
        }

        if (escapeSafe) {
          hasSafeEscape = true;
          break;
        }
      }

      if (hasSafeEscape) continue;

      // Check 2: Can any non-king piece capture the attacker(s)?
      let canNeutralize = false;
      for (const ourMove of ourMoves) {
        // Skip king moves (already checked above)
        if (
          ourMove.fromPosition.row === kingPosition.row &&
          ourMove.fromPosition.col === kingPosition.col
        )
          continue;

        for (const attackerPos of attackerPositions) {
          if (
            ourMove.move.position.row === attackerPos.row &&
            ourMove.move.position.col === attackerPos.col
          ) {
            canNeutralize = true;
            break;
          }
        }
        if (canNeutralize) break;
      }

      if (canNeutralize) continue;

      // No escape AND no neutralization → forced 2-move capture
      const enemyPieceDesc = `${enemyPiece.type} [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]->[${enemyMove.move.position.row},${enemyMove.move.position.col}]`;
      this.addLog(
        `   🔍 2-PLY TRAP: Enemy ${enemyPieceDesc} creates inescapable king threat`
      );
      return true;
    }

    return false;
  }

  private simulateKingMove(gameState: any, kingMove: MoveInfo): any | null {
    try {
      // Use proper deep copy from MCTSUtils
      const testState = cloneFullGameState(gameState);

      // Get the king piece properly
      const kingPiece = getPieceFromPosition(testState, kingMove.fromPosition);
      if (!kingPiece || !kingPiece.isKing) {
        return null;
      }

      // Check if this is a capture move
      const targetSquare =
        testState.board[kingMove.move.position.row][kingMove.move.position.col];
      const isCapture =
        targetSquare && targetSquare.Pieces && targetSquare.Pieces.length > 0;

      if (isCapture) {
        // Manual capture simulation for reliability
        targetSquare.Pieces = [];

        // Remove king from original position
        const fromSquare =
          testState.board[kingMove.fromPosition.row][kingMove.fromPosition.col];
        if (fromSquare && fromSquare.Pieces) {
          fromSquare.Pieces = fromSquare.Pieces.filter(
            (piece: any) => piece !== kingPiece
          );
        }

        // Place king at target position
        targetSquare.Pieces = [kingPiece];
      } else {
        // For non-capture moves, use the regular simulation
        try {
          applyMoveWithSnakeRespawn(
            testState,
            kingMove.fromPosition,
            kingMove.move,
            kingPiece
          );
        } catch (error) {
          return null;
        }
      }

      // Verify king is at the expected position
      const kingAtNewPos = MCTSUtils_v2.getPieceAt(
        testState.board,
        kingMove.move.position
      );

      if (
        !kingAtNewPos ||
        !kingAtNewPos.isKing ||
        kingAtNewPos.isLight !== kingPiece.isLight
      ) {
        return null;
      }

      // Validate any meadow effects
      validateMeadowEffects(testState);

      // Ensure turn switches to the enemy of the moving king
      testState.isLightTurn = !kingPiece.isLight;

      return testState;
    } catch (error) {
      return null;
    }
  }
}

